import { useState } from 'react';
import { Loader2, ChevronLeft, ChevronRight, RotateCw, AlertTriangle, DownloadCloud } from 'lucide-react';
import { docuwareInvoicesApi } from '../services/docuwareInvoicesApi';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useToast } from '../components/ui/Toast';
import { formatDateTimeCH } from '../utils/format';
import { useDocuWareInvoices, PAGE_SIZE } from '../hooks/useDocuWareInvoices';
import { DocuWareInvoiceFiltersBar } from '../components/docuware-invoices/DocuWareInvoiceFilters';
import { DocuWareInvoiceTable } from '../components/docuware-invoices/DocuWareInvoiceTable';
import { PoCheckTab } from '../components/docuware-invoices/po-check/PoCheckTab';
import { FilingCheckTab } from '../components/docuware-invoices/filing-check/FilingCheckTab';
import { docuWareFeedState } from '../types/docuwareInvoices';

/**
 * DocuWare invoice intake.
 *
 * Creditor invoices live in DocuWare from arrival until they are posted in BC, roughly 15
 * days later. This page lists what arrived, what it became in BC, and — the part nothing
 * else can answer — what has NOT been posted yet.
 *
 * Phase 1 was deliberately inline: one search box, one company filter, one fixed sort, no
 * hook and no components folder, because eight columns did not earn them. Phase 2 added
 * eleven filters, thirteen sortable columns and the BC link, which do; the state lives in
 * useDocuWareInvoices and the rendering in components/docuware-invoices/. The row expansion
 * is still the field-discovery UI: if the typed mapping is wrong, the real DocuWare field
 * names are readable there and fixed with an UPDATE.
 *
 * Phase 3 added the second tab. "Invoices" answers what arrived and what it became; "Order
 * Check" answers whether it agrees with the order we placed - on quantity and price, and on
 * the VAT treatment. That second question needed its own tab because its unit of work is
 * different: the invoice list is a register, the check is a queue somebody works through.
 *
 * Phase 5 added the third tab, and it runs the OTHER WAY ROUND. "Invoices" and "Order Check"
 * both start from a DocuWare document; "Filing Check" starts from a Business Central posting
 * and asks whether the invoice was ever filed at all - Heike's question from the 2026-08-25
 * feedback, which nothing in the app could answer while every path ran DocuWare -> BC.
 *
 * All dates come from SQL as strings and go through utils/format.ts. Never toISOString().
 */

type DwTab = 'invoices' | 'order-check' | 'filing-check';

const TABS: { key: DwTab; label: string }[] = [
    { key: 'invoices', label: 'Invoices' },
    { key: 'order-check', label: 'Order Check' },
    { key: 'filing-check', label: 'Filing Check' },
];

const TAB_SUBTITLES: Record<DwTab, string> = {
    'invoices': 'Creditor invoices in DocuWare and the Business Central postings they became.',
    'order-check': 'Goods invoices compared against the purchase order they were raised for.',
    'filing-check': 'Purchase invoices posted in Business Central with no document in DocuWare.',
};

export function DocuWareInvoicesPage() {
    const { canView, canEdit, isLoading: permLoading } = usePagePermissions('/docuware-invoices');
    const { toast } = useToast();
    const [activeTab, setActiveTab] = useState<DwTab>('invoices');

    const {
        rows, companies, facets, lastImport, total, loading, error,
        filters, setFilters, activeFilterCount,
        sortBy, sortOrder, setSort,
        page, setPage, totalPages, reload,
    } = useDocuWareInvoices(canView);

    const feed = docuWareFeedState(lastImport);

    // The manual ingest. Synchronous by design: the endpoint skips the daily sweep lanes, so
    // two days of arrivals finish well inside the SWA's request budget, and a spinner that
    // ends when the work ends beats a job id nobody polls.
    const [importing, setImporting] = useState(false);

    const importRecent = async () => {
        setImporting(true);
        try {
            const result = await docuwareInvoicesApi.importRecent();
            if (result.skipped) {
                toast(result.reason ?? 'Import skipped', 'error');
            } else {
                const parts = [`${result.inserted ?? 0} new`, `${result.updated ?? 0} updated`];
                // A company that failed is named, never folded into the totals — a partial
                // import that reports like a complete one is worse than no button.
                if (result.failedCompanies?.length) {
                    parts.push(`failed: ${result.failedCompanies.join(', ')}`);
                }
                toast(
                    `Imported the last ${result.lookbackDays} days (${parts.join(', ')})`,
                    result.failedCompanies?.length ? 'error' : 'success',
                );
            }
            await reload();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Import failed', 'error');
        } finally {
            setImporting(false);
        }
    };

    const copyDocId = async (docId: string) => {
        try {
            await navigator.clipboard.writeText(docId);
            toast(`Document ID ${docId} copied`, 'success');
        } catch {
            toast('Could not copy to clipboard', 'error');
        }
    };

    if (permLoading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
        );
    }

    if (!canView) {
        return (
            <div className="flex h-64 items-center justify-center">
                <p className="text-gray-500">You do not have access to this page.</p>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="mb-4 flex items-baseline justify-between gap-4">
                <h1 className="text-2xl font-bold text-gray-900">DocuWare Invoices</h1>
                {/* The old subtitle claimed these were invoices "not yet posted in Business
                    Central". Measured against BC, 92% of them are posted — the page shows the
                    whole pipeline, and "Not posted" is now a filter rather than a claim. */}
                <p className="text-xs italic text-gray-500">{TAB_SUBTITLES[activeTab]}</p>
            </div>

            <div className="mb-4 border-b border-gray-200">
                <nav className="-mb-px flex">
                    {TABS.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`border-b-2 px-6 py-3 text-sm font-medium transition-colors ${
                                activeTab === tab.key
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {activeTab === 'order-check' && (
                <PoCheckTab canEdit={canEdit} onToast={toast} onCopyDocId={copyDocId} />
            )}

            {/* Mounted only while open, unlike the invoice tab below: this one queries two BC
                replicas live, and a page that opens on "Invoices" must not pay for it. */}
            {activeTab === 'filing-check' && (
                <FilingCheckTab canEdit={canEdit} onToast={toast} onCopyDocId={copyDocId} />
            )}

            <div className={`space-y-4 ${activeTab === 'invoices' ? '' : 'hidden'}`}>
                <DocuWareInvoiceFiltersBar
                    filters={filters}
                    facets={facets}
                    companies={companies}
                    activeFilterCount={activeFilterCount}
                    onChange={setFilters}
                />

                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                    {/* "Reload" and not "Refresh": this re-runs the query, it does not fetch
                        from DocuWare. The button next to it is the one that does, which is why
                        the two are named differently and the last import time sits beside
                        them — otherwise a dead feed looks exactly like a quiet one. */}
                    <button
                        onClick={() => void reload()}
                        disabled={loading}
                        className="inline-flex h-8 items-center gap-2 rounded border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        title="Re-run the query against what is already stored. Use Import last 2 days to fetch from DocuWare."
                    >
                        <RotateCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        Reload table
                    </button>
                    {canEdit && (
                        <button
                            onClick={() => void importRecent()}
                            disabled={importing}
                            className="inline-flex h-8 items-center gap-2 rounded border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            title="Fetch the last 2 days from DocuWare for every company. The hourly import does this on weekdays 07:05-19:05; this is for when you cannot wait for the next tick."
                        >
                            <DownloadCloud className={`h-4 w-4 ${importing ? 'animate-pulse' : ''}`} />
                            {importing ? 'Importing…' : 'Import last 2 days'}
                        </button>
                    )}
                    <span>{total.toLocaleString('de-CH')} invoice{total === 1 ? '' : 's'}</span>
                    <span className="text-gray-300">·</span>
                    {/* A dead feed and a quiet one look identical on a list of invoices. The
                        import ran 401-ing for sixteen hours once and nothing here said so. */}
                    {feed.stale ? (
                        <span className="inline-flex items-center gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-amber-800">
                            <AlertTriangle className="h-4 w-4 shrink-0" />
                            <span>
                                No import for{' '}
                                {feed.ageHours === null
                                    ? 'as long as this page has existed'
                                    : `${Math.floor(feed.ageHours)} hours`}
                                {' '}— the hourly ingest should have run by now.
                                {lastImport && ` Last: ${formatDateTimeCH(lastImport)}`}
                            </span>
                        </span>
                    ) : (
                        <span>Last import: {lastImport ? formatDateTimeCH(lastImport) : 'never'}</span>
                    )}
                </div>

                {error && (
                    <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        {error}
                    </div>
                )}

                <div className="rounded-lg border border-gray-200 bg-white">
                    {loading && rows.length === 0 ? (
                        <div className="flex h-48 items-center justify-center">
                            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="flex h-48 items-center justify-center">
                            <p className="text-sm text-gray-500">
                                {activeFilterCount > 0
                                    ? 'No invoices match these filters.'
                                    : 'No invoices stored yet.'}
                            </p>
                        </div>
                    ) : (
                        <DocuWareInvoiceTable
                            rows={rows}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={setSort}
                            onCopyDocId={copyDocId}
                        />
                    )}
                </div>

                {total > PAGE_SIZE && (
                    <div className="flex items-center justify-between text-sm text-gray-600">
                        <span>
                            Page {page} of {totalPages}
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage(Math.max(1, page - 1))}
                                disabled={page <= 1 || loading}
                                className="inline-flex h-8 items-center gap-1 rounded border border-gray-300 px-2 hover:bg-gray-50 disabled:opacity-40"
                            >
                                <ChevronLeft className="h-4 w-4" /> Previous
                            </button>
                            <button
                                onClick={() => setPage(Math.min(totalPages, page + 1))}
                                disabled={page >= totalPages || loading}
                                className="inline-flex h-8 items-center gap-1 rounded border border-gray-300 px-2 hover:bg-gray-50 disabled:opacity-40"
                            >
                                Next <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
